import { memo, useCallback } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Heading, Menu, MenuItem, Popover, Switch, Text, useComponentStack } from '@/components/core';
import { DeleteIcon, EditIcon, OverflowIcon } from '@/components/icons';
import { EmailLogo, WebhookLogo } from '@/components/logos';
import { NavigationContainer } from '@/components/ui';
import { Notification, NOTIFICATION_TYPES, useValidationConfigStore } from '@/store';

import { NotificationConfiguration } from './NotificationConfiguration';

export type NotificationRowProps = {
  id: string;
};

const INotificationRow = ({ id }: NotificationRowProps) => {
  const notification = useValidationConfigStore((state) => state.getNotification(id));
  const deleteNotification = useValidationConfigStore((state) => state.deleteNotification);
  const stack = useComponentStack();

  const goToConfiguration = useCallback(() => {
    stack?.push(
      <NavigationContainer>
        <NotificationConfiguration id={id} />
      </NavigationContainer>
    );
  }, [id, stack]);

  const updateNotification = useValidationConfigStore((state) => state.updateNotification);

  const updateEnabledSettings = useCallback(
    ({ enabled }: Pick<Notification, 'enabled'>) => {
      updateNotification(id, (notification) => {
        const changes = {} as Partial<Notification>;
        if (enabled != null) changes.enabled = enabled;
        return { ...notification, ...changes };
      });
    },
    [id, updateNotification]
  );

  if (!notification?.type || !NOTIFICATION_TYPES.includes(notification.type)) return null;

  let logo;
  let heading;
  let description;
  switch (notification?.type) {
    case 'email':
      logo = <EmailLogo size="xl" />;
      heading = 'Email';
      description = 'Send email when a document needs review.';
      break;
    case 'http':
      logo = <WebhookLogo size="xl" />;
      heading = 'Webhook';
      description = 'Send HTTP request when a document needs review.';
      break;
  }

  return (
    <Button className="w-full text-start" variant="plain" onPress={goToConfiguration}>
      {logo}
      <div className="flex flex-col">
        <Heading className="!text-gray-700">{heading}</Heading>
        <Text className="!text-gray-500" size="xs">
          {description}
        </Text>
      </div>
      <div className="grow" />
      <Switch
        isSelected={notification?.enabled ?? true}
        onChange={(isSelected) => updateEnabledSettings({ enabled: isSelected })}
      />
      <MenuTrigger>
        <Button>
          <OverflowIcon className="rotate-90" />
        </Button>
        <Popover>
          <Menu>
            <MenuItem onAction={goToConfiguration}>
              <EditIcon />
              <Text>Edit</Text>
            </MenuItem>
            <MenuItem variant="error" onAction={() => deleteNotification(id)}>
              <DeleteIcon />
              <Text>Delete</Text>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>
    </Button>
  );
};

export const NotificationRow = memo(INotificationRow) as typeof INotificationRow;
